import * as THREE from "three";
import type { HitEffect } from "../game/match-controller";
import { OPPONENT_Z, PLAYER_Z } from "../game/world";

/** One small atlas shared by snow chunks, stars, rings and trail dots. */
export const particleAtlas = (() => {
  const data = new Uint8Array(96 * 32 * 4);
  for (let y = 0; y < 32; y++)
    for (let x = 0; x < 96; x++) {
      const tile = Math.floor(x / 32),
        xx = ((x % 32) + 0.5 - 16) / 16,
        yy = (y + 0.5 - 16) / 16,
        r = Math.hypot(xx, yy),
        a = Math.atan2(yy, xx);
      const edge = tile === 1 ? 0.58 + 0.28 * Math.cos(5 * a) : 1;
      const alpha =
        tile === 2
          ? r > 0.7 && r < 0.94
            ? 1
            : 0
          : tile === 0
            ? Math.max(0, 1 - r) ** 1.8
            : Math.max(0, Math.min(1, (edge - r) * 10));
      data.set([255, 255, 255, Math.round(alpha * 255)], (y * 96 + x) * 4);
    }
  const texture = new THREE.DataTexture(data, 96, 32);
  texture.repeat.set(1 / 3, 1);
  texture.needsUpdate = true;
  return texture;
})();
export function particleBatch(
  cap: number,
): THREE.InstancedMesh<THREE.PlaneGeometry, THREE.ShaderMaterial> {
  const geometry = new THREE.PlaneGeometry(1, 1);
  geometry.setAttribute(
    "tile",
    new THREE.InstancedBufferAttribute(new Float32Array(cap), 1),
  );
  geometry.setAttribute(
    "tint",
    new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3),
  );
  geometry.setAttribute(
    "alpha",
    new THREE.InstancedBufferAttribute(new Float32Array(cap), 1),
  );
  const material = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: { atlas: { value: particleAtlas } },
    vertexShader: `attribute float tile; attribute vec3 tint; attribute float alpha; varying vec2 vUv; varying float vTile; varying vec3 vTint; varying float vAlpha; void main(){vUv=uv;vTile=tile;vTint=tint;vAlpha=alpha;gl_Position=projectionMatrix*modelViewMatrix*instanceMatrix*vec4(position,1.0);}`,
    fragmentShader: `uniform sampler2D atlas; varying vec2 vUv; varying float vTile; varying vec3 vTint; varying float vAlpha; void main(){float a=texture2D(atlas,vec2((vUv.x+vTile)/3.0,vUv.y)).a*vAlpha;if(a<0.01)discard;gl_FragColor=vec4(vTint,a);}`,
  });
  const mesh = new THREE.InstancedMesh(geometry, material, cap);
  mesh.count = 0;
  mesh.frustumCulled = false;
  return mesh;
}
export function setParticle(
  mesh: THREE.InstancedMesh,
  index: number,
  matrix: THREE.Matrix4,
  tile: number,
  color: number,
  alpha: number,
): void {
  mesh.setMatrixAt(index, matrix);
  const c = new THREE.Color(color);
  (mesh.geometry.getAttribute("tile") as THREE.InstancedBufferAttribute).setX(
    index,
    tile,
  );
  (mesh.geometry.getAttribute("tint") as THREE.InstancedBufferAttribute).setXYZ(
    index,
    c.r,
    c.g,
    c.b,
  );
  (mesh.geometry.getAttribute("alpha") as THREE.InstancedBufferAttribute).setX(
    index,
    alpha,
  );
}
export function finishParticles(mesh: THREE.InstancedMesh): void {
  mesh.instanceMatrix.needsUpdate = true;
  for (const name of ["tile", "tint", "alpha"])
    mesh.geometry.getAttribute(name).needsUpdate = true;
}
export class HitParticles {
  readonly mesh = particleBatch(40);
  private readonly hits = new Map<number, HitEffect>();
  ko(
    t: number,
    loser: "player" | "opponent",
    camera: THREE.Camera,
    reduced: boolean,
  ): void {
    this.mesh.count = 0;
    if (t >= 650 && !reduced) return;
    const age = reduced ? 0 : Math.max(0, t - 150) / 1000,
      dummy = new THREE.Object3D();
    for (let i = 0; i < 34; i++) {
      const a = i * 2.39996,
        r = 0.16 + (i % 6) * 0.04 + (reduced ? 0 : age * (1.6 + (i % 4) * 0.3));
      dummy.position.set(
        Math.cos(a) * r,
        2.05 + Math.sin(a) * r - age * age * 2,
        (loser === "player" ? PLAYER_Z : OPPONENT_Z) + 0.45,
      );
      dummy.quaternion.copy(camera.quaternion);
      dummy.scale.setScalar(i < 24 ? 0.22 : 0.3);
      dummy.updateMatrix();
      setParticle(
        this.mesh,
        this.mesh.count++,
        dummy.matrix,
        i < 24 ? 0 : 1,
        i < 24 ? 0xffffff : 0xffd23f,
        reduced || t <= 150 ? 1 : Math.max(0, 1 - age / 0.5),
      );
    }
    finishParticles(this.mesh);
  }
  update(
    effects: HitEffect[],
    tick: number,
    camera: THREE.Camera,
    reduced: boolean,
  ): void {
    this.mesh.count = 0;
    for (const effect of effects) this.hits.set(effect.id, effect);
    const dummy = new THREE.Object3D();
    for (const [id, e] of this.hits) {
      const age = (tick - e.startTick) / 60;
      if (age < 0 || age > 0.4) {
        this.hits.delete(id);
        continue;
      }
      const size = 0.7,
        z = e.target === "player" ? PLAYER_Z : OPPONENT_Z;
      const count = reduced ? 6 : 16;
      for (let i = 0; i < count && this.mesh.count < 39; i++) {
        const a = i * 2.39996323,
          r = reduced ? 0.12 : age * (1.4 + (i % 3) * 0.4) * size;
        dummy.position.set(
          Math.cos(a) * r,
          1.65 + Math.sin(a) * r - (reduced ? 0 : age * age * 2),
          z + Math.sin(i * 3) * r * 0.3,
        );
        dummy.quaternion.copy(camera.quaternion);
        dummy.scale.setScalar((i < 10 ? 0.11 : 0.16) * size);
        dummy.updateMatrix();
        setParticle(
          this.mesh,
          this.mesh.count++,
          dummy.matrix,
          i < 10 ? 0 : 1,
          i < 10 ? 0xffffff : 0xffd640,
          1 - age / 0.4,
        );
      }
      if (!reduced && age < 0.18 && this.mesh.count < 40) {
        dummy.position.set(0, 1.65, z);
        dummy.quaternion.copy(camera.quaternion);
        dummy.scale.setScalar((age / 0.18) * size);
        dummy.updateMatrix();
        setParticle(
          this.mesh,
          this.mesh.count++,
          dummy.matrix,
          2,
          0xffffff,
          1 - age / 0.18,
        );
      }
    }
    finishParticles(this.mesh);
  }
}
